/**
 * Pure helpers for the two "don't waste a search" panels (contract §5):
 *   - overlap: creators this search found that already sit in other folders
 *   - near misses: creators that failed only a numeric threshold
 */
import { leadKey } from '../lib/platform';
import type {
  JobOverlap,
  NearMissChange,
  NearMisses,
  NearMissOption,
  OverlapCreator,
  SearchFilterDef,
  SearchParamValue,
  SearchSchema,
  WidenOption,
} from '../api/types';
import { compactNumber, fromWire, isFractionPercent } from './params';

// ── Overlap ───────────────────────────────────────────────────────────────

export function creatorKey(c: { platform?: string | null; username: string }): string {
  return leadKey(c.platform ?? undefined, c.username);
}

export interface OverlapSummary {
  total: number;
  named: { name: string; count: number }[];
  moreFolders: number;
  /** "23 already in other folders · KPOP broad pages (9) · movie edits (6) · +3 folders" */
  text: string;
}

export function overlapSummary(overlap: JobOverlap, maxNamed = 2): OverlapSummary {
  const folders = [...overlap.byFolder].sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
  const named = folders.slice(0, maxNamed).map((f) => ({ name: f.name, count: f.count }));
  const moreFolders = Math.max(0, folders.length - named.length);
  const lead =
    overlap.total === 1 ? '1 already in another folder' : `${overlap.total.toLocaleString()} already in other folders`;
  const parts = [lead, ...named.map((f) => `${f.name} (${f.count.toLocaleString()})`)];
  if (moreFolders > 0) parts.push(`+${moreFolders} folder${moreFolders === 1 ? '' : 's'}`);
  return { total: overlap.total, named, moreFolders, text: parts.join(' · ') };
}

export interface OverlapFolderGroup {
  folderPublicId: string;
  name: string;
  /** The server's count — can exceed `creators` when the creator list was capped. */
  count: number;
  creators: OverlapCreator[];
}

/** Creators grouped by the folder they're already in (a creator in two folders shows in both). */
export function groupOverlapByFolder(overlap: JobOverlap): OverlapFolderGroup[] {
  const groups = new Map<string, OverlapFolderGroup>();
  for (const f of overlap.byFolder) {
    groups.set(f.folderPublicId, { folderPublicId: f.folderPublicId, name: f.name, count: f.count, creators: [] });
  }
  for (const c of overlap.creators) {
    for (const f of c.folders) {
      let g = groups.get(f.publicId);
      if (!g) {
        g = { folderPublicId: f.publicId, name: f.name, count: 0, creators: [] };
        groups.set(f.publicId, g);
      }
      g.creators.push(c);
    }
  }
  for (const g of groups.values()) g.count = Math.max(g.count, g.creators.length);
  return [...groups.values()].sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
}

/** "Assigned to Ava · picked up by Ben", or null when nobody has them. */
export function whoHasThem(c: Pick<OverlapCreator, 'assignedToName' | 'claimedByName'>): string | null {
  const parts = [
    c.assignedToName ? `Assigned to ${c.assignedToName}` : null,
    c.claimedByName ? `picked up by ${c.claimedByName}` : null,
  ].filter(Boolean) as string[];
  if (parts.length === 0) return null;
  const text = parts.join(' · ');
  return text.charAt(0).toUpperCase() + text.slice(1);
}

// ── Near misses ───────────────────────────────────────────────────────────

/** Most creators first; a single change beats a combination with the same count. */
export function sortNearMissOptions(options: NearMissOption[]): NearMissOption[] {
  return [...options].sort((a, b) => b.count - a.count || a.changes.length - b.changes.length || a.id.localeCompare(b.id));
}

function humanise(key: string): string {
  return key.replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase();
}

/** "min avg views" — the filter's own label, lower-cased for use mid-sentence. */
export function filterPhrase(schema: SearchSchema | null, filter: string): string {
  const def = schema?.filters.find((f) => f.key === filter);
  const label = def?.label ?? humanise(filter);
  return label.charAt(0).toLowerCase() + label.slice(1);
}

/** A threshold value in the filter's own units ("5K", "30%"). */
export function formatThreshold(def: SearchFilterDef | undefined, n: number | null): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return 'none';
  if (def?.type === 'percent') return `${Math.round(isFractionPercent(def, n) ? n * 100 : n)}%`;
  if (def?.unit === '%') return `${n}%`;
  return compactNumber(n);
}

/** "min avg views 10K → 5K" (or "min avg views → 5K" when the old value is unknown). */
export function describeChange(schema: SearchSchema | null, c: NearMissChange): string {
  const def = schema?.filters.find((f) => f.key === c.filter);
  const phrase = filterPhrase(schema, c.filter);
  return c.from === null || c.from === undefined
    ? `${phrase} → ${formatThreshold(def, c.to)}`
    : `${phrase} ${formatThreshold(def, c.from)} → ${formatThreshold(def, c.to)}`;
}

export interface NearMissLabel {
  /** "+100" */
  count: string;
  /** "if min avg views 10K → 5K", or "if both" for a two-filter combination. */
  text: string;
  /** For combinations: each change, "min avg views 10K → 5K · max followers 250K → 500K". */
  detail: string | null;
}

export function nearMissLabel(schema: SearchSchema | null, option: NearMissOption): NearMissLabel {
  const count = `+${option.count.toLocaleString()}`;
  if (option.changes.length <= 1) {
    const c = option.changes[0];
    return { count, text: c ? `if ${describeChange(schema, c)}` : '', detail: null };
  }
  return {
    count,
    text: option.changes.length === 2 ? 'if both' : `if all ${option.changes.length}`,
    detail: option.changes.map((c) => describeChange(schema, c)).join(' · '),
  };
}

/** The value a near-miss creator missed on, formatted for its filter. */
export function formatMissedValue(schema: SearchSchema | null, option: NearMissOption, value: number | null): string {
  const filter = option.changes[0]?.filter;
  const def = schema?.filters.find((f) => f.key === filter);
  return formatThreshold(def, value);
}

/** Apply an option's relaxed thresholds to the form's params, for the next search. */
export function applyNearMiss(
  schema: SearchSchema,
  params: Record<string, SearchParamValue>,
  option: NearMissOption,
): Record<string, SearchParamValue> {
  const next = { ...params };
  for (const c of option.changes) {
    const def = schema.filters.find((f) => f.key === c.filter);
    next[c.filter] = def ? fromWire(def, c.to) : c.to;
  }
  return next;
}

/**
 * The legacy `widen` / `widenAnalysis` output as near-miss options, for a
 * server that predates `nearMisses`. Those creators can't be added without
 * re-searching (there's no include endpoint), so callers hide "Add".
 */
export function nearMissesFromWiden(
  widen: WidenOption[] | null | undefined,
  ranWith: Record<string, unknown> | null,
): NearMisses | null {
  if (!widen || widen.length === 0) return null;
  const options: NearMissOption[] = widen.map((w) => {
    const from = ranWith?.[w.filter];
    return {
      id: `${w.filter}:${w.value}`,
      changes: [{ filter: w.filter, from: typeof from === 'number' ? from : null, to: w.value }],
      count: w.gained,
      creators: w.sample.map((s) => ({
        username: s.username,
        platform: (s as { platform?: string }).platform ?? null,
        followers: s.followers,
        avgViews: s.avgViews,
        value: w.filter === 'minAvgViews' ? s.avgViews : s.followers,
      })),
    };
  });
  return { total: Math.max(0, ...options.map((o) => o.count)), options };
}
